import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { MuteButton } from './MuteButton';
import { CameraButton } from './CameraButton';
import { EndCallButton } from './EndCallButton';
import { DeviceMenu } from './DeviceMenu';
import type { DeviceInfo } from '../types';

interface ControlBarProps {
  isAudioOn: boolean;
  isVideoOn: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onEndCall: () => void;
  microphones: DeviceInfo[];
  speakers: DeviceInfo[];
  cameras: DeviceInfo[];
  selectedMic: string | null;
  selectedSpeaker: string | null;
  selectedCamera: string | null;
  onSelectMic: (deviceId: string) => void;
  onSelectSpeaker: (deviceId: string) => void;
  onSelectCamera: (deviceId: string) => void;
  className?: string;
  supportsSpeakerSelection?: boolean;
}

export function ControlBar({
  isAudioOn,
  isVideoOn,
  onToggleAudio,
  onToggleVideo,
  onEndCall,
  microphones,
  speakers,
  cameras,
  selectedMic,
  selectedSpeaker,
  selectedCamera,
  onSelectMic,
  onSelectSpeaker,
  onSelectCamera,
  className,
  supportsSpeakerSelection = true,
}: ControlBarProps) {
  const { t } = useTranslation();

  return (
    <div
      role="toolbar"
      aria-label={t('videoSession.labels.meetingControls', 'Meeting controls')}
      className={cn(
        'flex items-center justify-between gap-2 px-4 py-3 bg-black/80 backdrop-blur-sm',
        className
      )}
    >
      {/* Left side: Audio and Video controls */}
      <div className="flex items-center gap-3">
        {/* Audio control group */}
        <div className="flex items-center">
          <MuteButton isAudioOn={isAudioOn} onToggle={onToggleAudio} size="lg" />
          <DeviceMenu
            type="audio"
            microphones={microphones}
            speakers={speakers}
            selectedMic={selectedMic}
            selectedSpeaker={selectedSpeaker}
            onSelectMic={onSelectMic}
            onSelectSpeaker={onSelectSpeaker}
            supportsSpeakerSelection={supportsSpeakerSelection}
          />
        </div>

        {/* Video control group */}
        <div className="flex items-center">
          <CameraButton
            isVideoOn={isVideoOn}
            onToggle={onToggleVideo}
            size="lg"
          />
          <DeviceMenu
            type="video"
            cameras={cameras}
            selectedCamera={selectedCamera}
            onSelectCamera={onSelectCamera}
          />
        </div>
      </div>

      {/* Right side: End call */}
      <EndCallButton onEndCall={onEndCall} size="lg" />
    </div>
  );
}
